/**
 * useChatTurn — the transport-facing half of sending a chat turn, extracted
 * from AgentPage.sendMessage (Phase 2 chat-core split). Owns exactly the part
 * every chat surface repeats: quote-prefixing, attachment payload shaping,
 * wire-history assembly and the context envelope. The host keeps its own UI
 * concerns (queueing while streaming, clearing the composer, toasts).
 *
 * `getPageContext` is the embed hook: an embedded copilot passes a function
 * returning its page's ambient state ({ page, config, … }) and it rides as
 * `context.pageContext` on every turn. The standalone page passes nothing.
 */
import { useCallback } from 'react';
import { aiAPI } from '../services/api';
import { saveChatImage } from '../services/chatMediaCache';
import { buildWireHistory } from './wire';
import { knownFactsFrom } from './askLedger';

export function useChatTurn({ sendTurn, getPageContext } = {}) {
  const composeAndSend = useCallback(({
    text,
    quotedSelection,
    attachments = [],
    messages = [],
    activeCompanyCin = null,
    threadCompanyCin = null,
    webSearch = false,
    draftMode,
    companyOverrides,
    consent,
    sessionId,
  }) => {
    let trimmed = String(text || '').trim();
    if (!trimmed) return false;

    // A quoted artifact passage rides in front of the typed text so the model
    // knows exactly which part of the draft the user means.
    if (quotedSelection) {
      trimmed = `Regarding this part of the draft:\n> ${quotedSelection.replace(/\n/g, '\n> ')}\n\n${trimmed}`;
    }

    // Only ready attachments (text extracted, or image encoded) ride along.
    const ready = attachments.filter((a) => a.status === 'ready' && (a.text || a.data));
    const userMsg = {
      role: 'user',
      content: trimmed,
      // Keep the full payload on the message so follow-up turns still carry the
      // file (cross-turn memory). previewUrl uses the persistent data URL for
      // images so the sent chip keeps its thumbnail after the composer clears.
      attachments: ready.map((a) => ({
        id: a.id,
        name: a.name,
        fileType: a.fileType,
        kind: a.kind,
        text: a.text,
        truncated: a.truncated,
        mimeType: a.mimeType,
        data: a.data,
        previewUrl: a.fileType === 'image' ? a.dataUrl : null,
      })),
    };

    // Cache sent images locally (IndexedDB) so this chat's thumbnails come
    // back after a reload / history restore — the server keeps metadata only.
    ready.forEach((a) => {
      if (a.fileType === 'image' && a.dataUrl) saveChatImage(a.id, a.dataUrl, a.name);
    });

    // Persist sent DOCUMENTS server-side (S3/local via ChatAttachment) so a
    // restored conversation can reopen them on any device — same consent gate
    // as the transcript itself; a private chat persists nothing. Images ride
    // ChatMedia instead. Fire-and-forget: an upload failure must never touch
    // the turn. The raw File lives only on the composer object — the message
    // built above carries plain fields, so nothing binary enters React state.
    if (consent?.persist) {
      ready.forEach((a) => {
        if (a.fileType !== 'image' && a.file) {
          aiAPI.uploadChatAttachment(a.file, { attachmentId: a.id, sessionId })
            .catch(() => { /* cross-device restore just won't cover this file */ });
        }
      });
    }

    // History excludes the loading placeholder; attachments ride per-message
    // (bounded) so the model remembers files across turns. Context pins the company.
    const history = buildWireHistory([...messages, userMsg]);
    const known = knownFactsFrom(messages);
    const pageContext = typeof getPageContext === 'function' ? getPageContext() : null;
    const context = {
      platform: 'complyv',
      activeCompanyCin,
      // The company the user PICKED for this conversation, when they have.
      // Outranks activeCompanyCin server-side: the pin says where the user is
      // standing, this says which company the work is about.
      ...(threadCompanyCin ? { threadCompanyCin } : {}),
      // Everything the user has ANSWERED in this conversation's forms. The
      // fill-in form writes straight into the document and says nothing, so
      // without this the model never learns those values: it would redraft
      // from the company record and silently drop every one of them, and
      // re-ask for facts the user had already given.
      ...(Object.keys(known).length ? { knownFacts: known } : {}),
      webSearch,
      draftMode,
      ...(companyOverrides && Object.keys(companyOverrides).length ? { companyOverrides } : {}),
      ...(pageContext ? { pageContext } : {}),
    };

    sendTurn({ userMsg, history, context, consent, sessionId, userText: trimmed });
    return true;
  }, [sendTurn, getPageContext]);

  return { composeAndSend };
}

export default useChatTurn;
